import React, { useEffect, useState } from "react"
import { View, StyleSheet, TouchableOpacity } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import ClassChipGridM from "@common/components/funnel/ClassChipGridM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import { ModalSkeleton } from "@common/modals/ModalSkeleton"
import { theme } from "@common/theme/theme"

/**
 * Sélecteur COMPACT de matières d'une session — la refonte du « mur de chips »
 * inline. Toutes les matières proposables (icône + nom) vivent ICI, dans une
 * modale qui scale à 15+ options, plutôt que dépliées sous chaque carte.
 *
 * On RÉUTILISE le squelette de modale de l'app ({@see ModalSkeleton}, qui monte
 * ModalM + Portal) et la grille de pastilles existante ({@see ClassChipGridM},
 * multi-sélection plafonnée avec icônes) — aucun nouveau composant de modale
 * inventé.
 *
 * La modale édite une COPIE de travail (`staged`) ; rien n'est appliqué tant que
 * le parent n'a pas tapé « Valider ». Le plafond (`maxSubjects`, source backend)
 * grise les options non retenues une fois atteint. Bonus : « Appliquer à toutes
 * mes sessions » recopie la sélection sur les autres sessions cochées, pour
 * éviter de refaire le même choix N fois.
 */
const StageSubjectPickerModal = ({
    isVisible,
    onClose,
    subjectOptions = [],
    initialSelectedIds = [],
    maxSubjects = 1,
    loading = false,
    sessionLabel,
    /** « semaine » ou « week-end » — l'unité que les matières couvrent. */
    unitLabel = "semaine",
    canApplyToAll = false,
    onValidate,
}) => {
    const [staged, setStaged] = useState(initialSelectedIds)
    const [applyToAll, setApplyToAll] = useState(false)

    // Re-sème la copie de travail à chaque OUVERTURE — la modale part toujours
    // de la sélection réelle de la session, et « Appliquer à toutes » est
    // remis à zéro.
    useEffect(() => {
        if (isVisible) {
            setStaged(initialSelectedIds)
            setApplyToAll(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible])

    const atCap = staged.length >= maxSubjects
    const disabledIds = atCap
        ? subjectOptions
              .map((opt) => opt.id)
              .filter((id) => !staged.includes(id))
        : []

    const toggle = (id) => {
        setStaged((prev) => {
            if (prev.includes(id)) return prev.filter((s) => s !== id)
            if (prev.length >= maxSubjects) return prev
            return [...prev, id]
        })
    }

    // L'unité se dit, elle ne se devine pas : « cette semaine » sur une
    // formule vendue au créneau ferait croire que les matières valent pour le
    // seul créneau nommé au-dessus.
    //
    // Le PLAFOND, lui, ne s'annonce pas quand il y en a plusieurs : une ou deux
    // matières est ce qu'on conseillerait, et afficher « jusqu'à 4 » orienterait
    // le parent vers un étalement qu'on ne recommande pas. La case en trop
    // cesse simplement d'être cochable. Quand une seule est permise, on le dit —
    // là, c'est une contrainte à comprendre, pas une invitation.
    const capHint =
        maxSubjects > 1 ? null : `Une seule matière pour ce ${unitLabel}.`

    return (
        <ModalSkeleton
            isVisible={isVisible}
            onClose={onClose}
            title={
                maxSubjects > 1 ? "Choisir les matières" : "Choisir la matière"
            }
            loading={loading}
        >
            <View>
                {sessionLabel ? (
                    <TextM
                        fontWeight="Bold"
                        fontSize={12}
                        color={theme.colors.primary}
                        wrap
                        style={styles.sessionLabel}
                    >
                        {sessionLabel}
                    </TextM>
                ) : null}
                {capHint ? (
                    <TextM
                        fontSize={12}
                        color={theme.colors.grey}
                        wrap
                        style={styles.capHint}
                    >
                        {capHint}
                    </TextM>
                ) : null}

                {subjectOptions.length === 0 ? (
                    <TextM
                        fontSize={13}
                        color={theme.colors.grey}
                        wrap
                        style={styles.emptyText}
                    >
                        Aucune matière disponible pour cette classe pour le
                        moment.
                    </TextM>
                ) : (
                    <ClassChipGridM
                        options={subjectOptions}
                        selectedIds={staged}
                        disabledIds={disabledIds}
                        onToggle={toggle}
                    />
                )}

                {canApplyToAll ? (
                    <TouchableOpacity
                        style={styles.applyRow}
                        activeOpacity={0.8}
                        onPress={() => setApplyToAll((value) => !value)}
                    >
                        <FontAwesome5
                            name={applyToAll ? "check-square" : "square"}
                            solid={applyToAll}
                            size={16}
                            color={
                                applyToAll
                                    ? theme.colors.primary
                                    : theme.colors.lightMore
                            }
                        />
                        <TextM
                            fontSize={12}
                            color={theme.colors.grey}
                            wrap
                            style={styles.applyText}
                        >
                            Appliquer ces matières à tous mes {unitLabel}s
                            cochés
                        </TextM>
                    </TouchableOpacity>
                ) : null}

                <FilledButtonM
                    label="Valider"
                    onPress={() => onValidate?.(staged, { applyToAll })}
                    color={theme.colors.primary}
                    style={styles.validateButton}
                />
            </View>
        </ModalSkeleton>
    )
}

const styles = StyleSheet.create({
    sessionLabel: { marginBottom: 6 },
    capHint: { marginBottom: 14, lineHeight: 16 },
    emptyText: { paddingVertical: 12, lineHeight: 18 },
    applyRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginTop: 18,
    },
    applyText: { flexShrink: 1 },
    validateButton: { width: "100%", marginTop: 18, marginBottom: 0 },
})

export default StageSubjectPickerModal
